/**
 * UI operations for location redirect popup
 * @module location-redirect-popup/ui
 */

import { SELECTORS, CLASSES, FOCUSABLE } from './constants.js';
import type { PopupMode } from './constants.js';
import { saveChoice } from './storage.js';
import { buildRedirectUrl, interpolate } from './geolocation.js';
import type { WelcomeStrings } from './geolocation.js';
import type { UnsupportedLanguageStrings } from './unsupported-language.js';

export interface PopupState {
  currentCountry: string;
  currentCountryName: string;
  currentCurrencyCode: string;
  currentCurrencyName: string;
  currentLocale: string;
  targetMarket: { iso_code: string; displayName: string } | null;
  mode: PopupMode;
  previousActiveElement: HTMLElement | null;
  originalOverflow: string;
}

/**
 * Watches the Shopify country selector modal and saves the current country
 * when the modal is closed without a country selection (i.e. no navigation).
 * @param {string} currentCountry - The current country code to save as the choice
 */
function watchCountrySelectorClose(currentCountry: string): void {
  // Small delay to let the modal open after the trigger click
  setTimeout(function () {
    var modal = document.querySelector<HTMLElement>(
      SELECTORS.countrySelectorModal + '[aria-hidden="false"]',
    );
    if (!modal) {
      saveChoice(currentCountry);
      return;
    }

    var observer = new MutationObserver(function () {
      if (modal!.getAttribute('aria-hidden') === 'true') {
        observer.disconnect();
        saveChoice(currentCountry);
      }
    });

    observer.observe(modal, { attributes: true, attributeFilter: ['aria-hidden'] });
  }, 100);
}

/**
 * @typedef {Object} PopupState
 * @property {string} currentCountry - Current site's country code
 * @property {string} currentCountryName - Current site's country display name
 * @property {string} currentCurrencyCode - Current currency code (e.g., 'USD')
 * @property {string} currentCurrencyName - Current currency name (e.g., 'US Dollar')
 * @property {string} currentLocale - Current locale (e.g., 'en')
 * @property {Object} targetMarket - Target market info (detected, previously selected, or previous site)
 * @property {string} targetMarket.iso_code - Target country ISO code
 * @property {string} targetMarket.displayName - Target country display name
 * @property {string} mode - Popup mode ('geolocation', 'previous_selection', or 'pending_confirmation')
 * @property {HTMLElement|null} previousActiveElement - Element that had focus before popup
 * @property {string} originalOverflow - Original body overflow style
 */

/**
 * Populates dynamic content in the popup (target country name only)
 * All other content is rendered server-side via Liquid for translation support
 * @param {HTMLElement} popup - The popup element
 * @param {PopupState} state - Current popup state
 */
export function populateContent(popup: HTMLElement, state: PopupState): void {
  const targetCountrySpan = popup.querySelector<HTMLElement>(SELECTORS.targetCountry);
  if (targetCountrySpan) {
    targetCountrySpan.textContent = state.targetMarket!.displayName!;
  }
}

/**
 * Populates dynamic content in the welcome popup for first-time visitors
 * @param {HTMLElement} popup - The welcome popup element
 * @param {PopupState} state - Current popup state
 * @param {Object} welcomeStrings - Parsed welcome string templates
 */
export function populateWelcomeContent(
  popup: HTMLElement,
  state: PopupState,
  welcomeStrings: WelcomeStrings,
): void {
  const values = {
    detected_country: state.targetMarket!.displayName!,
    current_country: state.currentCountryName,
    currency_name: state.currentCurrencyName,
    currency_code: state.currentCurrencyCode,
  };

  const subtitle = popup.querySelector<HTMLElement>(SELECTORS.welcomeSubtitle);
  if (subtitle) {
    subtitle.textContent = interpolate(welcomeStrings.subtitle, values);
  }

  const currencyNote = popup.querySelector<HTMLElement>(SELECTORS.currencyNote);
  if (currencyNote) {
    currencyNote.textContent = interpolate(welcomeStrings.currency_note, values);
  }

  const shippingNote = popup.querySelector<HTMLElement>(SELECTORS.shippingNote);
  if (shippingNote) {
    shippingNote.textContent = interpolate(welcomeStrings.shipping_note, values);
  }

  const redirectBtn = popup.querySelector<HTMLElement>(SELECTORS.redirectBtn);
  if (redirectBtn) {
    redirectBtn.textContent = interpolate(welcomeStrings.go_to_site, values);
  }

  const stayBtn = popup.querySelector<HTMLElement>(SELECTORS.stayBtn);
  if (stayBtn) {
    stayBtn.textContent = interpolate(welcomeStrings.continue_to_site, values);
  }
}

/**
 * Populates dynamic content in the unsupported language popup
 * @param {HTMLElement} popup - The unsupported language popup element
 * @param {PopupState} state - Current popup state
 * @param {Object} strings - Parsed unsupported language string templates
 */
export function populateUnsupportedLanguageContent(
  popup: HTMLElement,
  state: PopupState,
  strings: UnsupportedLanguageStrings,
): void {
  const values = {
    current_country: state.currentCountryName,
    currency_name: state.currentCurrencyName,
    currency_code: state.currentCurrencyCode,
  };

  const subtitle = popup.querySelector<HTMLElement>(SELECTORS.unsupportedLangSubtitle);
  if (subtitle) {
    subtitle.textContent = interpolate(strings.subtitle, values);
  }

  const currencyNote = popup.querySelector<HTMLElement>(SELECTORS.unsupportedLangCurrencyNote);
  if (currencyNote) {
    currencyNote.textContent = interpolate(strings.currency_note, values);
  }

  const stayBtn = popup.querySelector<HTMLElement>(SELECTORS.stayBtn);
  if (stayBtn) {
    stayBtn.textContent = interpolate(strings.continue_btn, values);
  }
}

/**
 * Shows the popup with focus management
 * @param {HTMLElement} popup - The popup element
 * @param {PopupState} state - Current popup state (will be mutated)
 */
export function show(popup: HTMLElement, state: PopupState): void {
  state.previousActiveElement = document.activeElement as HTMLElement | null;
  state.originalOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  popup.classList.add(CLASSES.visible);
  popup.setAttribute('aria-hidden', 'false');

  // Focus the modal container (not a button) so tab starts inside modal
  popup.setAttribute('tabindex', '-1');
  setTimeout(function () {
    popup.focus();
  }, 100);

  console.info('[Location Redirect] Popup shown');
}

/**
 * Hides the popup and restores focus
 * @param {HTMLElement} popup - The popup element
 * @param {PopupState} state - Current popup state
 */
export function hide(popup: HTMLElement, state: PopupState): void {
  popup.classList.remove(CLASSES.visible);
  popup.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = state.originalOverflow;
  if (state.previousActiveElement) {
    state.previousActiveElement.focus();
  }

  console.info('[Location Redirect] Popup hidden');
}

/**
 * Binds event handlers to popup buttons and keyboard
 * @param {HTMLElement} popup - The popup element
 * @param {PopupState} state - Current popup state
 */
export function bindEvents(popup: HTMLElement, state: PopupState): void {
  const redirectBtn = popup.querySelector<HTMLElement>(SELECTORS.redirectBtn);
  const stayBtn = popup.querySelector<HTMLElement>(SELECTORS.stayBtn);
  const closeBtn = popup.querySelector<HTMLElement>(SELECTORS.closeBtn);

  if (redirectBtn) {
    redirectBtn.addEventListener('click', function () {
      saveChoice(state.targetMarket!.iso_code, { silent: true });
      window.location.href = buildRedirectUrl(state.currentLocale, state.targetMarket!.iso_code);
    });
  }

  if (stayBtn) {
    stayBtn.addEventListener('click', function () {
      saveChoice(state.currentCountry);
      hide(popup, state);
    });
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', function () {
      saveChoice(state.currentCountry);
      hide(popup, state);
    });
  }

  const returnBtn = popup.querySelector<HTMLElement>(SELECTORS.returnBtn);
  if (returnBtn) {
    if (!document.referrer) {
      // Remove from DOM entirely so it doesn't break the focus trap
      returnBtn.remove();
    } else {
      returnBtn.addEventListener('click', function () {
        // Navigate via a temporary <a> so the shared-cart module's delegated
        // click handler can intercept sister-site links and export the cart.
        // For non-sister-site referrers, the default link navigation fires.
        const link = document.createElement('a');
        link.href = document.referrer;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(function () {
          link.remove();
        }, 0);
      });
    }
  }

  const selectLocationBtn = popup.querySelector<HTMLElement>(SELECTORS.selectLocationBtn);
  if (selectLocationBtn) {
    selectLocationBtn.addEventListener('click', function () {
      hide(popup, state);
      // Open footer country selector by clicking its trigger button
      const footerCountryBtn = document.querySelector<HTMLElement>(SELECTORS.footerCountrySelector);
      if (footerCountryBtn) {
        footerCountryBtn.click();
        watchCountrySelectorClose(state.currentCountry);
      } else {
        // Footer selector not found — treat as "stay on current country"
        saveChoice(state.currentCountry);
      }
    });
  }

  // Keyboard: Escape saves current country and closes (same as "Continue"/"Stay"),
  // Tab traps focus within the popup
  document.addEventListener('keydown', function (event) {
    if (!popup.classList.contains(CLASSES.visible)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      saveChoice(state.currentCountry);
      hide(popup, state);
      return;
    }

    if (event.key === 'Tab') {
      const focusable = Array.from(popup.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const isInsideModal = popup.contains(document.activeElement);

      // If focus is outside modal, trap it inside
      if (!isInsideModal) {
        event.preventDefault();
        if (event.shiftKey) {
          last.focus();
        } else {
          first.focus();
        }
        return;
      }

      // Wrap focus at boundaries
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });
}
